import { useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, FileCheck2, Loader2, ShieldCheck, Eye } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { SignaturePad } from "@/components/SignaturePad";

const API = process.env.REACT_APP_BACKEND_URL;

const FORM_TYPES = ["w9", "w8ben", "w8bene"];

const emptyState = {
  w9: {
    name: "", business_name: "", tax_classification: "individual", llc_tax_class: "",
    other_description: "", exempt_payee_code: "", fatca_exempt_code: "", address: "",
    city_state_zip: "", account_numbers: "", ssn: "", ein: "",
  },
  w8ben: {
    name: "", country_of_citizenship: "", permanent_address: "", permanent_city_state_zip: "",
    permanent_country: "", mailing_address: "", mailing_city_state_zip: "", mailing_country: "",
    us_tin: "", foreign_tax_id: "", ftin_not_required: false, reference_number: "",
    date_of_birth: "", treaty_country: "", treaty_article: "", treaty_paragraph: "",
    treaty_income_type: "", treaty_rate: "", treaty_conditions: "",
  },
  w8bene: {
    org_name: "", country_of_incorporation: "", disregarded_entity_name: "", chapter3_status: "",
    permanent_address: "", permanent_city_state_zip: "", permanent_country: "", mailing_address: "",
    mailing_city_state_zip: "", mailing_country: "", chapter4_status: "", print_name: "",
  },
};

const Field = ({ label, children, required }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-neutral-400 text-xs">{label}{required && <span className="text-brand-red"> *</span>}</Label>
    {children}
  </div>
);

const inputClass = "bg-white/[0.03] border-white/10 text-white h-10";

export default function TaxForms() {
  const { t, language } = useLanguage();
  const tf = t.taxForms;
  const [formType, setFormType] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const sigRef = useRef(null);
  const formRef = useRef(null);

  const set = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const selectForm = (type) => {
    setFormType(type);
    setValues(emptyState[type]);
  };

  const goBack = () => {
    setFormType(null);
    setValues({});
  };

  const handlePreview = async () => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    const previewWindow = window.open("", "_blank", "noopener,noreferrer");
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/api/tax-forms/${formType}/preview`, values, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (previewWindow) {
        previewWindow.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Tax form preview failed:", err);
      previewWindow?.close();
      toast.error(tf.previewErrorToast);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error(tf.signatureRequiredToast);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...values, language, signature_image: sigRef.current.toDataURL() };
      const res = await axios.post(`${API}/api/tax-forms/${formType}`, payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${tf.forms[formType].title}_${(values.name || values.org_name || "").replace(/\s+/g, "_") || "form"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(tf.successToast);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Tax form submission failed:", err);
      if (err.response?.status === 429) {
        toast.error(tf.rateLimitToast);
      } else {
        toast.error(tf.errorToast);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="tax-forms-page">
      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-24">
        <Link to="/" data-testid="tax-forms-back-home" className="inline-flex items-center gap-2 text-neutral-500 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Doble R Records
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">{tf.pageTitle}</h1>
        <p className="text-neutral-400 text-base mb-10 max-w-xl">{tf.pageSubtitle}</p>

        {!formType ? (
          <div data-testid="tax-forms-selector">
            <h2 className="text-lg font-semibold mb-4">{tf.chooseTitle}</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {FORM_TYPES.map((type) => (
                <button
                  key={type}
                  data-testid={`select-form-${type}`}
                  onClick={() => selectForm(type)}
                  className="text-left p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-brand-red/50 hover:bg-brand-red/[0.04] transition-colors"
                >
                  <FileCheck2 className="w-5 h-5 text-brand-red mb-3" />
                  <p className="font-heading font-bold text-lg mb-1">{tf.forms[type].title}</p>
                  <p className="text-neutral-400 text-xs mb-2">{tf.forms[type].subtitle}</p>
                  <p className="text-neutral-500 text-xs leading-relaxed">{tf.forms[type].useWhen}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit} data-testid={`tax-form-${formType}`} className="space-y-8">
            <button type="button" onClick={goBack} data-testid="tax-forms-back-selector" className="text-neutral-500 hover:text-white text-sm transition-colors">
              {tf.backToForms}
            </button>

            {formType === "w9" && <W9Fields values={values} set={set} tf={tf} sigRef={sigRef} />}
            {formType === "w8ben" && <W8BenFields values={values} set={set} tf={tf} sigRef={sigRef} />}
            {formType === "w8bene" && <W8BeneFields values={values} set={set} tf={tf} sigRef={sigRef} />}

            <div className="flex items-start gap-2 rounded-lg border border-brand-blue/20 bg-brand-blue/[0.04] p-3">
              <ShieldCheck className="w-4 h-4 text-brand-blue shrink-0 mt-0.5" />
              <p className="text-neutral-400 text-xs leading-relaxed">{tf.privacyNote}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                onClick={handlePreview}
                disabled={previewLoading}
                data-testid="tax-form-preview-button"
                variant="outline"
                className="flex-1 h-12 rounded-full border-white/15 bg-white/[0.02] hover:bg-white/[0.06] text-white font-semibold"
              >
                {previewLoading ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {tf.previewing}</span>
                ) : (
                  <span className="flex items-center gap-2"><Eye className="w-4 h-4" /> {tf.previewButton}</span>
                )}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="tax-form-submit-button"
                className="flex-1 h-12 rounded-full bg-brand-red hover:bg-brand-red/90 text-white font-semibold"
              >
                {submitting ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {tf.submitting}</span>
                ) : tf.submitButton}
              </Button>
            </div>
            <p className="text-neutral-600 text-[11px] text-center leading-relaxed">{tf.disclaimer}</p>
          </form>
        )}
      </div>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold text-brand-red uppercase tracking-wide mt-2">{children}</h3>
);

const TAX_CLASS_KEYS = ["individual", "c_corp", "s_corp", "partnership", "trust_estate", "llc", "other"];
const CHAPTER3_KEYS = [
  "corporation", "partnership", "simple_trust", "tax_exempt_organization", "complex_trust",
  "foreign_gov_controlled_entity", "central_bank_of_issue", "private_foundation", "estate",
  "foreign_gov_integral_part", "grantor_trust", "disregarded_entity", "international_organization",
];
const CHAPTER4_KEYS = ["active_nffe", "passive_nffe", "other"];

function W9Fields({ values, set, tf, sigRef }) {
  const f = tf.fields;
  return (
    <>
      <SectionTitle>{tf.sections.identification}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.name} required>
          <Input data-testid="w9-name" required value={values.name} onChange={(e) => set("name", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.businessName}>
          <Input data-testid="w9-business-name" value={values.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputClass} />
        </Field>
      </div>
      <Field label={f.taxClassification} required>
        <Select value={values.tax_classification} onValueChange={(v) => set("tax_classification", v)}>
          <SelectTrigger data-testid="w9-tax-classification" className={inputClass}><SelectValue /></SelectTrigger>
          <SelectContent>
            {TAX_CLASS_KEYS.map((k) => <SelectItem key={k} value={k}>{tf.taxClassificationOptions[k]}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      {values.tax_classification === "llc" && (
        <Field label={f.llcTaxClass}>
          <Input data-testid="w9-llc-class" maxLength={1} value={values.llc_tax_class} onChange={(e) => set("llc_tax_class", e.target.value.toUpperCase())} className={inputClass} />
        </Field>
      )}
      {values.tax_classification === "other" && (
        <Field label={f.otherDescription}>
          <Input data-testid="w9-other-description" value={values.other_description} onChange={(e) => set("other_description", e.target.value)} className={inputClass} />
        </Field>
      )}

      <SectionTitle>{tf.sections.address}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.address} required>
          <Input data-testid="w9-address" required value={values.address} onChange={(e) => set("address", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.cityStateZip} required>
          <Input data-testid="w9-city-state-zip" required value={values.city_state_zip} onChange={(e) => set("city_state_zip", e.target.value)} className={inputClass} />
        </Field>
      </div>
      <Field label={f.accountNumbers}>
        <Input data-testid="w9-account-numbers" value={values.account_numbers} onChange={(e) => set("account_numbers", e.target.value)} className={inputClass} />
      </Field>

      <SectionTitle>{tf.sections.taxInfo}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.ssn}>
          <Input data-testid="w9-ssn" placeholder="123-45-6789" value={values.ssn} onChange={(e) => set("ssn", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.ein}>
          <Input data-testid="w9-ein" placeholder="12-3456789" value={values.ein} onChange={(e) => set("ein", e.target.value)} className={inputClass} />
        </Field>
      </div>

      <SectionTitle>{tf.sections.certification}</SectionTitle>
      <SignaturePad ref={sigRef} label={f.signature} testId="w9-signature-pad" clearTestId="w9-signature-clear" hint={tf.signatureHint} />
    </>
  );
}

function W8BenFields({ values, set, tf, sigRef }) {
  const f = tf.fields;
  return (
    <>
      <SectionTitle>{tf.sections.identification}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.name} required>
          <Input data-testid="w8ben-name" required value={values.name} onChange={(e) => set("name", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.countryOfCitizenship} required>
          <Input data-testid="w8ben-citizenship" required value={values.country_of_citizenship} onChange={(e) => set("country_of_citizenship", e.target.value)} className={inputClass} />
        </Field>
      </div>
      <Field label={f.dateOfBirth} required>
        <Input data-testid="w8ben-dob" required placeholder="MM-DD-YYYY" value={values.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className={inputClass} />
      </Field>

      <SectionTitle>{tf.sections.address}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.address} required>
          <Input data-testid="w8ben-address" required value={values.permanent_address} onChange={(e) => set("permanent_address", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.cityStateZip} required>
          <Input data-testid="w8ben-city" required value={values.permanent_city_state_zip} onChange={(e) => set("permanent_city_state_zip", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.country} required>
          <Input data-testid="w8ben-country" required value={values.permanent_country} onChange={(e) => set("permanent_country", e.target.value)} className={inputClass} />
        </Field>
      </div>

      <SectionTitle>{tf.sections.mailingAddress}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.mailingAddress}>
          <Input data-testid="w8ben-mailing-address" value={values.mailing_address} onChange={(e) => set("mailing_address", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.mailingCityStateZip}>
          <Input data-testid="w8ben-mailing-city" value={values.mailing_city_state_zip} onChange={(e) => set("mailing_city_state_zip", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.mailingCountry}>
          <Input data-testid="w8ben-mailing-country" value={values.mailing_country} onChange={(e) => set("mailing_country", e.target.value)} className={inputClass} />
        </Field>
      </div>

      <SectionTitle>{tf.sections.taxInfo}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.usTin}>
          <Input data-testid="w8ben-us-tin" value={values.us_tin} onChange={(e) => set("us_tin", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.foreignTaxId}>
          <Input data-testid="w8ben-foreign-tax-id" value={values.foreign_tax_id} onChange={(e) => set("foreign_tax_id", e.target.value)} className={inputClass} />
        </Field>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox data-testid="w8ben-ftin-not-required" checked={values.ftin_not_required} onCheckedChange={(v) => set("ftin_not_required", !!v)} />
        <Label className="text-neutral-400 text-xs">{f.ftinNotRequired}</Label>
      </div>
      <Field label={f.referenceNumber}>
        <Input data-testid="w8ben-reference-number" value={values.reference_number} onChange={(e) => set("reference_number", e.target.value)} className={inputClass} />
      </Field>

      <SectionTitle>{tf.sections.treaty}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.treatyCountry}>
          <Input data-testid="w8ben-treaty-country" value={values.treaty_country} onChange={(e) => set("treaty_country", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.treatyArticle}>
          <Input data-testid="w8ben-treaty-article" value={values.treaty_article} onChange={(e) => set("treaty_article", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.treatyParagraph}>
          <Input data-testid="w8ben-treaty-paragraph" value={values.treaty_paragraph} onChange={(e) => set("treaty_paragraph", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.treatyRate}>
          <Input data-testid="w8ben-treaty-rate" value={values.treaty_rate} onChange={(e) => set("treaty_rate", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.treatyIncomeType}>
          <Input data-testid="w8ben-treaty-income-type" value={values.treaty_income_type} onChange={(e) => set("treaty_income_type", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.treatyConditions}>
          <Input data-testid="w8ben-treaty-conditions" value={values.treaty_conditions} onChange={(e) => set("treaty_conditions", e.target.value)} className={inputClass} />
        </Field>
      </div>

      <SectionTitle>{tf.sections.certification}</SectionTitle>
      <SignaturePad ref={sigRef} label={f.signature} testId="w8ben-signature-pad" clearTestId="w8ben-signature-clear" hint={tf.signatureHint} />
    </>
  );
}

function W8BeneFields({ values, set, tf, sigRef }) {
  const f = tf.fields;
  return (
    <>
      <SectionTitle>{tf.sections.identification}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.orgName} required>
          <Input data-testid="w8bene-org-name" required value={values.org_name} onChange={(e) => set("org_name", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.countryOfIncorporation} required>
          <Input data-testid="w8bene-country-incorporation" required value={values.country_of_incorporation} onChange={(e) => set("country_of_incorporation", e.target.value)} className={inputClass} />
        </Field>
      </div>
      <Field label={f.disregardedEntityName}>
        <Input data-testid="w8bene-disregarded-entity" value={values.disregarded_entity_name} onChange={(e) => set("disregarded_entity_name", e.target.value)} className={inputClass} />
      </Field>
      <Field label={f.chapter3Status} required>
        <Select value={values.chapter3_status} onValueChange={(v) => set("chapter3_status", v)}>
          <SelectTrigger data-testid="w8bene-chapter3-status" className={inputClass}><SelectValue /></SelectTrigger>
          <SelectContent>
            {CHAPTER3_KEYS.map((k) => <SelectItem key={k} value={k}>{tf.chapter3Options[k]}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label={f.chapter4Status}>
        <Select value={values.chapter4_status} onValueChange={(v) => set("chapter4_status", v)}>
          <SelectTrigger data-testid="w8bene-chapter4-status" className={inputClass}><SelectValue /></SelectTrigger>
          <SelectContent>
            {CHAPTER4_KEYS.map((k) => <SelectItem key={k} value={k}>{tf.chapter4Options[k]}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-neutral-600 text-[11px] leading-relaxed">{tf.chapter4Hint}</p>
      </Field>

      <SectionTitle>{tf.sections.address}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.address} required>
          <Input data-testid="w8bene-address" required value={values.permanent_address} onChange={(e) => set("permanent_address", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.cityStateZip} required>
          <Input data-testid="w8bene-city" required value={values.permanent_city_state_zip} onChange={(e) => set("permanent_city_state_zip", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.country} required>
          <Input data-testid="w8bene-country" required value={values.permanent_country} onChange={(e) => set("permanent_country", e.target.value)} className={inputClass} />
        </Field>
      </div>

      <SectionTitle>{tf.sections.mailingAddress}</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.mailingAddress}>
          <Input data-testid="w8bene-mailing-address" value={values.mailing_address} onChange={(e) => set("mailing_address", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.mailingCityStateZip}>
          <Input data-testid="w8bene-mailing-city" value={values.mailing_city_state_zip} onChange={(e) => set("mailing_city_state_zip", e.target.value)} className={inputClass} />
        </Field>
        <Field label={f.mailingCountry}>
          <Input data-testid="w8bene-mailing-country" value={values.mailing_country} onChange={(e) => set("mailing_country", e.target.value)} className={inputClass} />
        </Field>
      </div>

      <SectionTitle>{tf.sections.certification}</SectionTitle>
      <Field label={f.printName} required>
        <Input data-testid="w8bene-print-name" required value={values.print_name} onChange={(e) => set("print_name", e.target.value)} className={inputClass} />
      </Field>
      <SignaturePad ref={sigRef} label={f.signature} testId="w8bene-signature-pad" clearTestId="w8bene-signature-clear" hint={tf.signatureHint} />
    </>
  );
}
